import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import { TbChevronRight } from "react-icons/tb";
import { StablesHistoryListSkeleton } from "../../../../../Components/Molecules/DashboardSkeleton";
import { StablesGeneralHistoryGrid } from "../../../Stables/components/StablesGeneralHistoryGrid";
import { StablesHistoryPagination } from "../../../Stables/components/StablesHistoryPagination";
import { paginateList } from "../../../Extract/extractFilters";
import { BAAS_STABLES_TRANSACTIONS_PATH } from "../../constants";
import { mapBaasOperationsToStablesHistory } from "../baasStablesHistoryMapper";

const DEFAULT_ROWS_PER_PAGE = 10;

const OPERATION_HISTORY_TYPES = {
  deposit: ["deposit"],
  buy: ["buy"],
  sell: ["sell"],
  swap: ["swap"],
  withdraw: ["transfer"],
};

const HISTORY_TITLE_KEYS = {
  deposit: "baas.stables.operation.history.deposits",
  buy: "baas.stables.operation.history.purchases",
  sell: "baas.stables.operation.history.sales",
  swap: "baas.stables.operation.history.swaps",
  withdraw: "baas.stables.operation.history.withdrawals",
};

const buildViewAllPath = (walletId) => {
  if (!walletId) return BAAS_STABLES_TRANSACTIONS_PATH;
  return `${BAAS_STABLES_TRANSACTIONS_PATH}?walletId=${encodeURIComponent(walletId)}`;
};

export const BaasStablesOperationHistoryPanel = ({
  wallet,
  operation,
  refreshToken = 0,
}) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [loading, setLoading] = useState(false);

  const historyTitle = intl.get(HISTORY_TITLE_KEYS[operation] ?? "baas.stables.operation.history.general");

  const historyItems = useMemo(() => {
    if (!wallet) return [];

    const allowedTypes = OPERATION_HISTORY_TYPES[operation];
    const operations = wallet.recentOperations ?? [];

    const filtered = allowedTypes
      ? operations.filter((item) => allowedTypes.includes(item.type))
      : operations;

    return mapBaasOperationsToStablesHistory(filtered);
  }, [wallet, operation]);

  useEffect(() => {
    setPage(1);
  }, [wallet?.id, operation]);

  useEffect(() => {
    if (refreshToken <= 0) return;

    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 350);
    return () => window.clearTimeout(timer);
  }, [refreshToken]);

  const totalPages = Math.max(1, Math.ceil(historyItems.length / rowsPerPage));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const displayItems = useMemo(
    () => paginateList(historyItems, page, rowsPerPage),
    [historyItems, page, rowsPerPage],
  );

  if (!wallet) return null;

  return (
    <section className="min-w-0">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-bold text-brand-dark dark:text-gray-100">
          {historyTitle}
        </h2>
        <Link
          to={buildViewAllPath(wallet.id)}
          className="link-no-zoom inline-flex items-center gap-1 text-xs font-semibold text-[#e7a73f] transition hover:text-brand-dark dark:hover:text-gray-200"
        >
          {intl.get("baas.stablesWallet.recentActivity.viewAll")}
          <TbChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="app-card overflow-hidden">
        {loading ? (
          <StablesHistoryListSkeleton rows={rowsPerPage} />
        ) : historyItems.length === 0 ? (
          <div className="app-empty-state m-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.stables.operation.history.empty")}
            </p>
          </div>
        ) : (
          <StablesGeneralHistoryGrid items={displayItems} showDetail={false} />
        )}

        {historyItems.length > 0 ? (
          <div className="mt-4">
            <StablesHistoryPagination
              total={historyItems.length}
              page={page}
              rowsPerPage={rowsPerPage}
              currentPageCount={displayItems.length}
              totalPages={totalPages}
              onPageChange={setPage}
              onRowsPerPageChange={(value) => {
                setRowsPerPage(value);
                setPage(1);
              }}
              itemLabel={intl.get("stables.operationsItemLabel")}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};
